import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Spinner } from "react-bootstrap";
import { HiOutlineThumbUp, HiOutlineThumbDown } from "react-icons/hi";
import { BiCategoryAlt } from "react-icons/bi";
import { FaUser } from "react-icons/fa";
import { toast } from "react-toastify";
import "./Duplicatecomplaintmodal.css"; 

/**
 * Read-only view of a matching complaint, shown to a student when their
 * new submission looks like a duplicate of something already open.
 *
 * Props:
 *  - show: boolean
 *  - complaint: the matchingComplaint object returned by the 409 response
 *  - similarity: string/number, e.g. "0.84"
 *  - onClose: () => void
 *  - onSupported: () => void  — called after the user dismisses the success popup (parent resets form)
 *  - onRegisterAnyway: () => void — called when user picks "No, my complaint is different"
 *  - baseUrl: string (REACT_APP_COMPLAINTS_APP_BE_URL)
 */
const Duplicatecomplaintmodal = ({
  show,
  complaint,
  similarity,
  onClose,
  onSupported,
  onRegisterAnyway,
  baseUrl,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [supportAdded, setSupportAdded] = useState(false);

  // Reset the success state every time the modal is (re)opened for a new duplicate
  useEffect(() => {
    if (show) {
      setSupportAdded(false);
    }
  }, [show]);

  if (!complaint) return null;

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const visibleComments = (complaint.comments || []).filter(
    (c) =>
      c.text &&
      !c.text.includes("[Assistant Update]") &&
      !c.text.toLowerCase().includes("updated status to")
  );
  const topTwoComments = visibleComments.slice(0, 2);

  const similarityPercent = similarity
    ? Math.round(parseFloat(similarity) * 100)
    : null;

  const handleConfirmSame = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");

      await axios.post(
        `${baseUrl}/user-api/support-complaint/${complaint.complaint_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSupportAdded(true);
    } catch (error) {
      console.error("Error supporting complaint:", error);
      if (error.response?.status === 409) {
        // User already supported this complaint before — still show the success
        // state so they're not stuck staring at the same screen.
        toast.info(error.response.data.message || "You've already supported this complaint.");
        setSupportAdded(true);
      } else {
        toast.error("Failed to register your support. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOkClick = () => {
    setSupportAdded(false);
    onSupported();
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable dialogClassName="dup-modal-dialog">
      <Modal.Header closeButton className="dup-modal-header">
        <Modal.Title className="dup-modal-title">
          {supportAdded ? "Support added" : "Similar complaint found"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="dup-modal-body">
        {supportAdded ? (
          <div className="dup-success-popup text-center">
            <div className="dup-success-icon">✅</div>

            <h3 className="dup-success-heading">Thank you!</h3>

            <p className="dup-success-text">
              Your issue has been linked to this complaint. We've notified the
              admin that another student is experiencing the same problem and added like to complaint,
              helping increase its priority for faster resolution.
            </p>

            <button className="btn dup-btn-success" onClick={handleOkClick}>
              OK
            </button>
          </div>
        ) : (
          <>
            <div className="dup-modal-top d-flex justify-content-between align-items-center mb-3">
              <span className="dup-modal-category">
                <BiCategoryAlt size={15} className="me-1" />
                {complaint.category}
              </span>

              <div className="d-flex gap-3">
                <span className="dup-modal-engagement">
                  <HiOutlineThumbUp size={17} /> {complaint.likes ?? 0}
                </span>
                <span className="dup-modal-engagement">
                  <HiOutlineThumbDown size={17} /> {complaint.dislikes ?? 0}
                </span>
              </div>
            </div>

            <h5 className="dup-modal-heading">{complaint.title}</h5>

            <div className="dup-modal-meta mb-2 d-flex align-items-center flex-wrap gap-2">
              <span className="dup-modal-date">
                <i className="bi bi-calendar3 me-1"></i>
                {formatDate(complaint.timestamp)}
              </span>
              <span className={`dup-modal-status status-${(complaint.status || "").toLowerCase()}`}>
                {complaint.status}
              </span>
              {typeof complaint.supportCount === "number" && complaint.supportCount > 0 && (
                <span className="dup-modal-support-count">
                  🔥 {complaint.supportCount} other student{complaint.supportCount > 1 ? "s" : ""} reported this
                </span>
              )}
            </div>

            <p className="dup-modal-description">{complaint.description}</p>

            {complaint.category === "IT and Networking" && complaint.it_details && (
              <div className="dup-modal-it-details mb-3">
                <h6 className="dup-modal-subheading">IT and networking details</h6>
                <div className="row">
                  {complaint.it_details.location && (
                    <div className="col-md-6 mb-1">
                      <strong className="dup-modal-label">Location:</strong> {complaint.it_details.location}
                    </div>
                  )}
                  {complaint.it_details.connectionType && (
                    <div className="col-md-6 mb-1">
                      <strong className="dup-modal-label">Connection:</strong> {complaint.it_details.connectionType}
                    </div>
                  )}
                  {complaint.it_details.room_number && (
                    <div className="col-md-6 mb-1">
                      <strong className="dup-modal-label">Room:</strong> {complaint.it_details.room_number}
                    </div>
                  )}
                  {complaint.it_details.issue_duration && (
                    <div className="col-md-6 mb-1">
                      <strong className="dup-modal-label">Duration:</strong> {complaint.it_details.issue_duration}
                    </div>
                  )}
                </div>
              </div>
            )}

            {similarity && (
              <div className="dup-modal-similarity mb-3">
                <div className="dup-modal-similarity-row">
                  <span>Similarity </span>
                  <span className="dup-modal-similarity-value">{similarityPercent}%</span>
                </div>
                <div className="dup-modal-similarity-track">
                  <div
                    className="dup-modal-similarity-fill"
                    style={{ width: `${similarityPercent}%` }}
                  ></div>
                </div>
              </div>
            )}

            <hr className="dup-modal-divider" />

            <h6 className="dup-modal-subheading mb-2">
              <i className="bi bi-chat-left-dots-fill me-1"></i>
              Timeline ({visibleComments.length})
            </h6>

            {visibleComments.length > 0 ? (
              <div className="dup-modal-timeline">
                {topTwoComments.map((c, index) => (
                  <div key={c.id || index} className="dup-modal-comment">
                    <span className="dup-modal-comment-dot">
                      <FaUser size={10} />
                    </span>
                    <div className="dup-modal-comment-body">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <strong className="dup-modal-comment-author">
                          {c.role === "student" ? "Student" : c.email || "Admin"}
                        </strong>
                        <span className="dup-modal-comment-date">{formatDate(c.timestamp || c.date)}</span>
                      </div>
                      <div className="dup-modal-comment-text">{c.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dup-modal-empty">No comments yet.</p>
            )}
          </>
        )}
      </Modal.Body>

      {!supportAdded && (
        <div className="dup-modal-decision text-center">
          <p className="dup-modal-decision-question mb-3">Does this complaint describe your issue?</p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <button
              type="button"
              className="btn dup-btn-success dup-modal-btn"
              onClick={handleConfirmSame}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                <>✅ Yes, this is the same issue.</>
              )}
            </button>

            <button
              type="button"
              className="btn dup-btn-outline dup-modal-btn"
              onClick={onRegisterAnyway}
              disabled={submitting}
            >
              ➕ No, my complaint is different
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default Duplicatecomplaintmodal;